import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'

import { cn } from '@/lib/utils'

type Variant = 'primary' | 'gold' | 'outline' | 'ghost' | 'outline-light'
type Size = 'sm' | 'md' | 'lg'

interface CommonProps {
    variant?: Variant
    size?: Size
    className?: string
    children: ReactNode
    withArrow?: boolean
}

const base =
    'group relative inline-flex items-center justify-center gap-2 rounded-full font-medium tracking-tight transition-all duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-50 disabled:pointer-events-none'

const sizes: Record<Size, string> = {
    sm: 'h-9 px-4 text-sm',
    md: 'h-11 px-6 text-[0.95rem]',
    lg: 'h-14 px-8 text-base'
}

const variants: Record<Variant, string> = {
    primary: 'bg-navy text-parchment hover:-translate-y-0.5 hover:bg-navy-deep',
    gold: 'bg-gold text-ink hover:-translate-y-0.5 hover:bg-gold-soft',
    outline: 'border border-navy/20 text-navy hover:border-navy hover:bg-navy hover:text-parchment',
    'outline-light':
        'border border-parchment/30 text-parchment hover:border-parchment hover:bg-parchment hover:text-ink',
    ghost: 'text-navy hover:bg-navy/[0.06]'
}

const Inner = ({ children, withArrow }: { children: ReactNode; withArrow?: boolean }) => (
    <>
        <span className='relative z-10 inline-flex items-center gap-2'>{children}</span>
        {withArrow && (
            <ArrowRight className='relative z-10 size-4 transition-transform duration-300 group-hover:translate-x-1 rtl:-scale-x-100 rtl:group-hover:-translate-x-1' />
        )}
    </>
)

interface ButtonProps extends CommonProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
    href?: undefined
}

interface LinkButtonProps extends CommonProps, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'href'> {
    href: string
    external?: boolean
}

export const Button = (props: ButtonProps | LinkButtonProps) => {
    const {
        variant = 'primary',
        size = 'md',
        className,
        children,
        withArrow,
        ...rest
    } = props

    const classes = cn(base, sizes[size], variants[variant], className)

    if ('href' in props && props.href) {
        const { href, external, ...anchorRest } = rest as LinkButtonProps

        if (external) {
            return (
                <a
                    href={href}
                    target='_blank'
                    rel='noopener noreferrer'
                    className={classes}
                    {...anchorRest}
                >
                    <Inner withArrow={withArrow}>{children}</Inner>
                </a>
            )
        }

        return (
            <Link href={href} className={classes} {...anchorRest}>
                <Inner withArrow={withArrow}>{children}</Inner>
            </Link>
        )
    }

    return (
        <button className={classes} {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}>
            <Inner withArrow={withArrow}>{children}</Inner>
        </button>
    )
}
